import { useEffect, useRef, useState } from "react";
import { useAccount, useDisconnect, useSwitchChain } from "wagmi";
import { openAppKit, closeAppKit } from "@/lib/wallet";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Wallet, LogOut, AlertTriangle } from "lucide-react";
import { useWalletRuntime } from "@/components/WalletProvider";
import { friendlyWalletMessage, reportWalletError } from "@/lib/wallet-diagnostics";
import { clearAllAuthAndWalletSessions, markPendingWalletRedirect } from "@/lib/session-cleanup";
import logoAsset from "@/assets/robotdex-logo.jpg.asset.json";

const STORAGE_KEY = "robotdex.selectedChainId";
const ADDRESS_KEY = "robotdex.lastAddress";
const ADDRESS_EVENT = "robotdex:wallet-address";

function notifyAddressChanged() {
  window.dispatchEvent(new Event(ADDRESS_EVENT));
}

async function openWalletCatalog() {
  try {
    markPendingWalletRedirect();
    await openAppKit({ view: "AllWallets" });
  } catch (error) {
    reportWalletError(error, { context: "openAppKit:AllWallets" });
    const message = error instanceof Error ? error.message : String(error);
    toast.error(friendlyWalletMessage(error), {
      description: message,
      duration: 8000,
    });
  }
}

export function getSelectedChainId(): number | null {
  if (typeof window === "undefined") return null;
  const v = Number(localStorage.getItem(STORAGE_KEY) || "");
  return v > 0 ? v : null;
}

function short(addr: string) {
  return `${addr.slice(0, 7)}…${addr.slice(-4)}`;
}

const CHAIN_COLORS: Record<number, string> = {
  1: "#627EEA",
  56: "#F3BA2F",
  8453: "#0052FF",
  42161: "#28A0F0",
};

const CHAIN_SHORT: Record<number, string> = {
  1: "ETH",
  56: "BNB",
  8453: "Base",
  42161: "ARB",
};

function chainLabel(id?: number, fallback?: string) {
  if (!id) return fallback ?? "Network";
  return CHAIN_SHORT[id] ?? fallback ?? `Chain ${id}`;
}

function NetworkSwitcher() {
  const { chain } = useAccount();
  if (!chain) return null;
  const color = CHAIN_COLORS[chain.id] ?? "#00b8ff";
  const label = chainLabel(chain.id, chain.name);
  return (
    <button
      type="button"
      onClick={() => openAppKit({ view: "Networks" })}
      title="Switch network"
      className="neon-border inline-flex items-center gap-2 h-9 px-3 rounded-md text-xs hover:bg-primary/10 transition"
    >
      <span
        className="inline-block h-3 w-3 rounded-full"
        style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }}
      />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ConnectButtonInner() {
  const { error } = useWalletRuntime();
  const { address, isConnected, chain } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  

  const prevConnected = useRef(false);
  const prevAddress = useRef<string | undefined>(undefined);
  const prevChainId = useRef<number | undefined>(undefined);
  const restoredRef = useRef(false);

  // Connect / disconnect / address-change toasts + persist address
  useEffect(() => {
    if (isConnected && address && !prevConnected.current) {
      toast.success("Wallet connected", { description: short(address) });
      localStorage.setItem(ADDRESS_KEY, address);
      notifyAddressChanged();
    } else if (!isConnected && prevConnected.current) {
      toast("Wallet disconnected");
      localStorage.removeItem(ADDRESS_KEY);
      notifyAddressChanged();
      restoredRef.current = false;
      // Auto-purge stale WalletConnect / Reown session state on ANY disconnect
      // (button, AppKit Account modal, wallet-app side, session expiry). This
      // prevents "Forbidden: Session verification failed" on the next connect.
      closeAppKit().catch(() => undefined);
      void clearAllAuthAndWalletSessions();
    } else if (isConnected && address && prevAddress.current && prevAddress.current !== address) {
      toast.success("Wallet switched", { description: short(address) });
      localStorage.setItem(ADDRESS_KEY, address);
      notifyAddressChanged();
    }
    prevConnected.current = isConnected;
    prevAddress.current = address;
  }, [isConnected, address]);

  // Restore saved chain once after connect
  useEffect(() => {
    if (!isConnected || restoredRef.current || !switchChain) return;
    const saved = Number(localStorage.getItem(STORAGE_KEY) || "");
    if (saved && chain && saved !== chain.id) {
      try {
        switchChain({ chainId: saved });
      } catch {
        /* ignore */
      }
    }
    restoredRef.current = true;
  }, [isConnected, chain, switchChain]);

  // Persist + toast on chain change
  useEffect(() => {
    if (!chain) return;
    if (prevChainId.current !== undefined && prevChainId.current !== chain.id) {
      toast.success("Network changed", { description: chainLabel(chain.id, chain.name) });
    }
    localStorage.setItem(STORAGE_KEY, String(chain.id));
    prevChainId.current = chain.id;
  }, [chain]);

  if (!isConnected || !address) {
    return (
      <div className="flex flex-col items-end gap-2">
        <Button
          onClick={openWalletCatalog}
          className="relative overflow-hidden bg-gradient-to-r from-primary to-primary/70 text-primary-foreground font-semibold tracking-wide uppercase text-xs neon-glow hover:brightness-110 transition"
        >
          <img src={logoAsset.url} alt="" aria-hidden className="h-4 w-4 mr-2 rounded-sm object-cover" />
          Connect Wallet
        </Button>


        {error && (
          <details className="max-w-[min(92vw,28rem)] rounded-md border border-destructive/50 bg-destructive/10 p-2 text-left text-xs text-destructive-foreground shadow-lg">
            <summary className="cursor-pointer font-medium text-destructive">Wallet error details</summary>
            <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed">
              {JSON.stringify(error, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <NetworkSwitcher />
      <Button variant="outline" size="sm" onClick={() => openAppKit({ view: "Account" })} className="neon-border">
        <Wallet className="h-4 w-4 mr-1" /> {short(address)}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={async () => {
          try {
            disconnect();
          } finally {
            await closeAppKit();
            await clearAllAuthAndWalletSessions();
          }
        }}
        title="Disconnect"
      >
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function ConnectButton() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) {
    return (
      <Button disabled className="bg-gradient-to-r from-primary/70 to-primary/50 text-primary-foreground font-semibold tracking-wide uppercase text-xs">
        <Wallet className="h-4 w-4 mr-2" /> Connect Wallet
      </Button>
    );
  }
  return <ConnectButtonInner />;
}

function ChainMismatchBannerInner() {
  const { ready } = useWalletRuntime();
  const { isConnected, chain } = useAccount();
  const { switchChain, isPending } = useSwitchChain();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedId(getSelectedChainId());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSelectedId(getSelectedChainId());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Keep in sync when the current chain updates (we write to localStorage there)
  useEffect(() => {
    if (chain) setSelectedId((prev) => prev ?? chain.id);
  }, [chain]);

  if (!ready || !isConnected || !chain || !selectedId || selectedId === chain.id) return null;

  return (
    <div className="w-full border-b border-yellow-500/40 bg-yellow-500/10 text-yellow-100 px-3 py-2 flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-400" />
        <span className="truncate">
          Wallet is on <b>{chainLabel(chain.id, chain.name)}</b> but selected network is{" "}
          <b>{chainLabel(selectedId)}</b>.
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="neon-border shrink-0"
        disabled={isPending}
        onClick={() => {
          const target = selectedId;
          switchChain(
            { chainId: target },
            {
              onSuccess: () => toast.success(`Switched to ${chainLabel(target)}`),
              onError: (err: unknown) => {
                const e = err as { code?: number; message?: string };
                if (e?.code === 4001 || /reject|denied/i.test(e?.message ?? "")) {
                  toast.error("Network switch rejected");
                } else if (e?.code === 4902 || /unrecognized|unknown chain|not.*added/i.test(e?.message ?? "")) {
                  toast.error(`${chainLabel(target)} is not available in your wallet`);
                } else {
                  toast.error("Failed to switch network", { description: e?.message });
                }
              },
            },
          );
        }}
      >
        Switch to {chainLabel(selectedId)}
      </Button>
    </div>
  );
}

export function ChainMismatchBanner() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <ChainMismatchBannerInner />;
}

